import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { eventService } from '../services/database';
import { Event as SupabaseEvent } from '../lib/supabase';
import { 
  faArrowLeft, faCalendarDays, faLocationDot, faUsers, 
  faFilter, faSearch, faTrophy, faRoute, faClock,
  faChevronRight, faMapMarkerAlt, faGaugeHigh, faCalendarCheck,
  faUserGroup, faCheckCircle, faExclamationTriangle, faTimesCircle,
  faSpinner, faInfoCircle, faBell, faGear, faUser
} from '@fortawesome/free-solid-svg-icons';

// Event type definition for display
interface DisplayEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  difficulty: string;
  registered: number;
  capacity: number;
  deadline: string;
  image: string;
  badge?: {
    text: string;
    color: string;
  };
}

const Events: React.FC = () => {
  // State for category filter
  const [activeCategory, setActiveCategory] = useState('All Events');
  // State for search query
  const [searchQuery, setSearchQuery] = useState('');
  // State for events from database
  const [events, setEvents] = useState<DisplayEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load events from database
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const supabaseEvents = await eventService.getAllEvents();
      
      // Transform Supabase events to display format
      const displayEvents: DisplayEvent[] = supabaseEvents.map(event => ({
        id: event.id,
        title: event.title,
        date: formatDate(event.date),
        time: formatTime(event.date),
        location: event.location,
        difficulty: getDifficultyFromDescription(event.description),
        registered: event.current_participants,
        capacity: event.max_participants,
        deadline: formatDate(event.registration_deadline),
        image: getEventImage(event.title),
        badge: getEventBadge(event)
      }));
      
      setEvents(displayEvents);
      setError(null);
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getDifficultyFromDescription = (description: string) => {
    if (description.toLowerCase().includes('beginner')) return 'Beginner';
    if (description.toLowerCase().includes('intermediate')) return 'Intermediate';
    if (description.toLowerCase().includes('expert') || description.toLowerCase().includes('advanced')) return 'Expert';
    return 'All Levels';
  };

  const getEventImage = (title: string) => {
    // Default images based on event type
    if (title.toLowerCase().includes('kids') || title.toLowerCase().includes('family')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/1e47f714b4-6bd43a89fc178a2bf2cb.png';
    }
    if (title.toLowerCase().includes('championship') || title.toLowerCase().includes('race')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/d82ea563d8-78875ce6151593f0a740.png';
    }
    if (title.toLowerCase().includes('workshop') || title.toLowerCase().includes('training')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/85a91caa1e-62302dc765829cb4a791.png';
    }
    return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/0143432743-43182f97ff610c1ad975.png';
  };

  const getEventBadge = (event: SupabaseEvent) => {
    const now = new Date();
    const registrationDeadline = new Date(event.registration_deadline);
    const eventDate = new Date(event.date);
    
    if (eventDate < now) {
      return { text: 'Past Event', color: 'bg-gray-500' };
    }
    
    if (registrationDeadline < now) {
      return { text: 'Registration Closed', color: 'bg-red-500' };
    }
    
    const percentFull = (event.current_participants / event.max_participants) * 100;
    
    if (percentFull >= 90) {
      return { text: 'Almost Full', color: 'bg-red-500' };
    }
    
    if (percentFull >= 70) {
      return { text: 'Filling Up', color: 'bg-yellow-500' };
    }
    
    if (event.description.toLowerCase().includes('family') || event.description.toLowerCase().includes('kids')) {
      return { text: 'Family Friendly', color: 'bg-secondary' };
    }
    
    return { text: 'Registration Open', color: 'bg-primary' };
  };

  // Categories for filter buttons
  const categories = ['All Events', 'Beginner', 'Intermediate', 'Expert', 'Family'];

  // Filter events based on search query and active category
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = activeCategory === 'All Events' || 
                           event.difficulty.includes(activeCategory);
    
    return matchesSearch && matchesCategory;
  });

  // Handle filter toggle
  const handleFilterToggle = () => {
    alert('Filter options would appear here');
  };

  // Handle category selection
  const handleCategorySelect = (category: string) => {
    setActiveCategory(category);
  };

  return (
    <>
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
            </Link>
          <h1 className="app-header-title">Events</h1>
          <button className="text-primary hover:text-orange-400 transition-colors">
            <FontAwesomeIcon icon={faFilter} className="text-lg" />
            </button>
        </div>
      </header>

      <main className="content-container pb-28">
        <div className="my-4">
          <div className="relative">
            <input 
              type="search" 
              placeholder="Search events..." 
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3.5 text-gray-400" />
          </div>
        </div>

        <div className="mb-6 overflow-x-auto">
          <div className="flex space-x-2 py-2">
            {categories.map((category) => (
              <button 
                key={category}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
                  activeCategory === category 
                    ? 'bg-primary text-white' 
                    : 'bg-white border border-gray-300'
                }`}
                onClick={() => handleCategorySelect(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Upcoming Events</h2>
            {loading && (
              <div className="text-sm text-gray-500">Loading...</div>
            )}
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
              <button 
                onClick={loadEvents}
                className="ml-2 underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}
          
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-500">Loading events...</p>
            </div>
          ) : filteredEvents.length > 0 ? (
            filteredEvents.map((event) => (
              <div key={event.id} className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
                <Link to={`/events/${event.id}`} className="block">
                  <div className="relative h-40">
                    <img 
                      className="w-full h-full object-cover" 
                      src={event.image} 
                      alt={event.title} 
                    />
                    {event.badge && (
                      <div className={`absolute top-3 right-3 ${event.badge.color} text-white text-xs font-semibold px-2 py-1 rounded`}>
                        {event.badge.text}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg">{event.title}</h3>
                    <div className="flex items-center text-gray-600 text-sm mt-2">
                      <FontAwesomeIcon icon={faCalendarDays} className="mr-2" />
                      <span>{event.date} • {event.time}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm mt-2">
                      <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm mt-2">
                      <FontAwesomeIcon icon={faGaugeHigh} className="mr-2" />
                      <span>{event.difficulty}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm mt-2">
                      <FontAwesomeIcon icon={faUsers} className="mr-2" />
                      <span>{event.registered}/{event.capacity} Registered</span>
                    </div>
                    <div className="mt-4 flex justify-between items-center">
                      <span className="text-sm font-medium">Registration Deadline: {event.deadline}</span>
                      <Link to={`/events/${event.id}`} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium">
                        Details
                      </Link>
                    </div>
                  </div>
                </Link>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No events found matching your criteria.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="app-bottom-nav">
        <div className="flex justify-around items-center px-2 py-3">
          <Link to="/dashboard" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faGear} className="text-xl" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link-active py-1">
            <FontAwesomeIcon icon={faCalendarDays} className="text-xl" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/team" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faUserGroup} className="text-xl" />
            <span className="text-xs mt-1">My Team</span>
          </Link>
          <Link to="/profile" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faUser} className="text-xl" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
        </div>
      </footer>
    </>
  );
};

export default Events;
